import {
  AlertColor,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DropDownInput from 'components/DropDownInput';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { FormDataModel } from '../../../../components/Form';
import axiosInstance from '../../../../utils/axios';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { images } from '../../../../utils/constants/images';
import { URLS } from '../../../../utils/constants/urls';
import { Privilege } from '../../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from '../../../../utils/redux/reducer/filter-slice';
import { getListData } from '../../../../utils/validators/HelperFunctions';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { usageTableColumn } from '../../CommonPage/usageColumn';
import { AddEditTaxGroupForm, TAX_SCOPE_OPTIONS } from './AddEditTaxGroup/AddEditTaxGroupForm';

interface TaxCodeRow {
  clientKey: string;
  id?: number;
  taxCodeId: number | '';
  scope: string;
  sortOrder: number;
}

const dropDownDefaults = {
  validators: [] as never[],
  onFocus: undefined as undefined,
  inputProps: undefined as undefined,
  disabled: false,
  className: undefined as undefined,
  labelId: undefined as undefined,
  style: undefined as undefined,
  sx: undefined as undefined,
  hasError: false,
  fieldError: false,
};

const TaxGroupList: FC = () => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const userPrivileges = useSelector(Privilege) as string[];

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [taxCodeOptions, setTaxCodeOptions] = useState<{ label: string; value: number }[]>([]);
  const [taxCodeRows, setTaxCodeRows] = useState<TaxCodeRow[]>([]);
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const scopeOptions = useMemo(() => TAX_SCOPE_OPTIONS(strings), []);
  const formModel = useMemo(
    () =>
      AddEditTaxGroupForm(strings).map(field => ({
        ...field,
        responsive: { xs: 12 },
      })),
    [],
  );

  const getList = useCallback(
    (filterData: PageProps) => {
      const body = filterData
        ? filterData
        : { page: index + 1, size: pageSize, filter: filter ?? [], sort: ['createdAt:desc'] };
      getListData(setLoading, body, URLS.taxGroup, setPageResponse);
    },
    [index, pageSize, filter],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get(URLS.taxGroupTaxCodes);
        setTaxCodeOptions((data || []).map((item: any) => ({ label: item.name, value: item.id })));
      } catch {
        // ignore
      }
    })();
  }, []);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const openDrawer = useCallback(async (recordId: number | null) => {
    setEditingId(recordId);
    if (recordId) {
      try {
        setDrawerLoading(true);
        const { data } = await axiosInstance.get(URLS.getTaxGroupById(recordId));
        setFormValues({ name: data?.name ?? '' });
        setTaxCodeRows(
          (data.taxCodes || []).map((row: any, rowIndex: number) => ({
            clientKey: row.id != null ? `id-${row.id}` : `row-${rowIndex}`,
            id: row.id,
            taxCodeId: row.taxCodeId,
            scope: row.scope,
            sortOrder: row.sortOrder ?? rowIndex,
          })),
        );
      } catch (error: any) {
        handleCatch(error);
        return;
      } finally {
        setDrawerLoading(false);
      }
    } else {
      setFormValues({ name: '' });
      setTaxCodeRows([]);
    }
    setDrawerOpen(true);
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
    setTaxCodeRows([]);
  };

  const addTaxCodeRow = () => {
    setTaxCodeRows(rows => [
      ...rows,
      {
        clientKey: `new-${Date.now()}`,
        taxCodeId: '',
        scope: 'ALWAYS',
        sortOrder: rows.length,
      },
    ]);
  };

  const updateTaxCodeRow = (clientKey: string, patch: Partial<TaxCodeRow>) => {
    setTaxCodeRows(rows => rows.map(row => (row.clientKey === clientKey ? { ...row, ...patch } : row)));
  };

  const deleteTaxCodeRow = (clientKey: string) => {
    setTaxCodeRows(rows => rows.filter(row => row.clientKey !== clientKey));
  };

  const isDuplicateTaxCode = (rows: TaxCodeRow[], taxCodeId: number | '', clientKey: string) => {
    if (taxCodeId === '') return false;
    return rows.some(row => row.clientKey !== clientKey && row.taxCodeId === taxCodeId);
  };

  const handleSave = async (body: FormDataModel) => {
    if (!taxCodeRows.length) {
      setMessage({ display: true, severity: 'error', message: strings.taxGroup_tax_codes_required });
      return;
    }
    for (const row of taxCodeRows) {
      if (row.taxCodeId === '' || !row.scope) {
        setMessage({ display: true, severity: 'error', message: strings.taxGroup_tax_code_row_incomplete });
        return;
      }
      if (isDuplicateTaxCode(taxCodeRows, row.taxCodeId, row.clientKey)) {
        setMessage({ display: true, severity: 'error', message: strings.taxGroup_duplicate_tax_code });
        return;
      }
    }

    try {
      setSaving(true);
      const payload = {
        name: body.name,
        taxCodes: taxCodeRows.map((row, rowIndex) => ({
          id: row.id ?? 0,
          taxCodeId: row.taxCodeId,
          scope: row.scope,
          sortOrder: rowIndex,
        })),
      };
      if (editingId) {
        await axiosInstance.put(URLS.editTaxGroup(editingId), payload);
      } else {
        await axiosInstance.post(URLS.addTaxGroup, payload);
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      await axiosInstance.delete(URLS.editTaxGroup(editingId));
      setMessage({ display: true, severity: 'success', message: strings.common_delete_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.taxGroup_title_list,
      subtitle: strings.taxGroup_list_subtitle,
      searchField: 'name',
      searchPlaceholder: 'Search tax groups',
      AddButtonText: strings.taxGroup_button_add,
      listApi: URLS.taxGroup,
      deleteApi: URLS.editTaxGroup,
      addPrivilege: privileges.add_tax_code,
      editPrivilege: privileges.edit_tax_code,
      deletePrivilege: privileges.delete_tax_code,
      customAdd: () => openDrawer(null),
      customEdit: (id: number) => openDrawer(id),
      tableColumn: [
        {
          field: 'name',
          headerName: strings.taxGroup_field_name,
          flex: 4,
          sortable: true,
        },
        usageTableColumn(
          row => [{ count: row.taxCodeCount, singular: 'Tax Code', plural: 'Tax Codes' }],
          'Tax Codes',
        ),
        lastUpdatedTableColumn(),
      ],
    }),
    [openDrawer],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage
        isLoading={loading}
        isWhiteSpace={false}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.taxGroup_title_edit : strings.taxGroup_title_add}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.delete_tax_code)}
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
        width="50%">
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {strings.taxGroup_section_tax_codes}
          </Typography>
          <Button variant="outlined" size="small" onClick={addTaxCodeRow} sx={{ mb: 2 }}>
            {strings.taxGroup_button_add_tax_code}
          </Button>
          {taxCodeRows.length === 0 ? (
            <Box
              sx={{
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
                color: 'text.secondary',
              }}>
              <Typography variant="body2">{strings.taxGroup_empty_tax_codes}</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 1.5, fontWeight: 600 }}>{strings.taxGroup_column_tax_code}</TableCell>
                  <TableCell sx={{ py: 1.5, fontWeight: 600 }}>{strings.taxGroup_column_scope}</TableCell>
                  <TableCell align="right" sx={{ py: 1.5, fontWeight: 600 }}>
                    {strings.customermgmt_column_action}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {taxCodeRows.map(row => {
                  const duplicate = isDuplicateTaxCode(taxCodeRows, row.taxCodeId, row.clientKey);
                  return (
                    <TableRow key={row.clientKey}>
                      <TableCell sx={{ minWidth: 200, py: 1.5, verticalAlign: 'top' }}>
                        <DropDownInput
                          {...dropDownDefaults}
                          label={strings.taxGroup_column_tax_code}
                          size="small"
                          field={`${row.clientKey}-taxCode`}
                          id={`${row.clientKey}-taxCode`}
                          inputValue={row.taxCodeId === '' ? '' : row.taxCodeId}
                          options={taxCodeOptions}
                          placeholder={strings.budget_placeholder_select}
                          hasError={duplicate}
                          fieldError={duplicate}
                          onChange={(value: string | number) => {
                            updateTaxCodeRow(row.clientKey, {
                              taxCodeId: value === '' ? '' : Number(value),
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 180, py: 1.5, verticalAlign: 'top' }}>
                        <DropDownInput
                          {...dropDownDefaults}
                          label={strings.taxGroup_column_scope}
                          size="small"
                          field={`${row.clientKey}-scope`}
                          id={`${row.clientKey}-scope`}
                          inputValue={row.scope}
                          options={scopeOptions}
                          placeholder={strings.budget_placeholder_select}
                          onChange={(value: string | number) => {
                            updateTaxCodeRow(row.clientKey, { scope: String(value) });
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.5, verticalAlign: 'middle' }}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ pt: 0.5 }}>
                          <img
                            src={images.deleteIcon}
                            alt="delete"
                            width={20}
                            height={20}
                            style={{ cursor: 'pointer' }}
                            onClick={() => deleteTaxCodeRow(row.clientKey)}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Box>
      </MasterDataEntityDrawer>
    </>
  );
};

export default TaxGroupList;
